import * as Yup from 'yup'
import { Form, Row, Collapse, Accordion, Table, InputGroup } from 'react-bootstrap'
import { CustomAccordionButton, CustomFormControl, CustomLink, CustomFileControlText, CustomFileControlButton } from '../../../../StyledComponents';
import { useState, useEffect, useRef } from 'react';
import { characterRestrictionInDocNamesRegExp } from '../ValidationRules';

const applicationPermittedExtensions = ['.pdf']
const applicationPermittedMimeTypes = ['application/pdf']
const applicationFileNumberLimit = 11

function getFileSizeAsString(totalBytes: number): string {
    const Gigabyte = 1073741824
    const Megabyte = 1048576
    const Kilobyte = 1024

    if (totalBytes <= 0) return "0 Bytes"

    if (totalBytes >= Gigabyte) {
        return (totalBytes / Gigabyte).toFixed(2) + " GB"
    } else if (totalBytes >= Megabyte) {
        return (totalBytes / Megabyte).toFixed(2) + " MB"
    } else if (totalBytes >= Kilobyte) {
        return (totalBytes / Kilobyte).toFixed(2) + " kB"
    } else {
        return totalBytes + " Bytes"
    }
}

const checkRequiredDokument = (settings) => (Yup.mixed()
    .required(`Document is required and must not be larger than ${getFileSizeAsString(settings.applicationFileSizeLimit)}.`)
    .test("FileSize", `Document must not be larger than ${getFileSizeAsString(settings.applicationFileSizeLimit)}`, (file) => file && file.size < settings.applicationFileSizeLimit)
    .test("NullByteTest", "The file must not be empty.", (file) => {
        if (file) {
            if (file.isValid !== undefined && file.isValid !== null && !file.isValid) return false;
            file.isValid = file.size > 0;
            if (!file.isValid) file.errMessage = "The file must not be empty.";
            return file.isValid;
        }
    })
    .test("FileFormat", `The following file formats are allowed: ${applicationPermittedExtensions.join(" ")}`, (file) => {
        if (file) {
            if (file.isValid !== undefined && file.isValid !== null && !file.isValid) return false;
            file.isValid = applicationPermittedMimeTypes.includes(file.type);
            if (!file.isValid) file.errMessage = `The following file formats are allowed: ${applicationPermittedExtensions.join(" ")}`;
            return file.isValid;
        }
    })
    .when("settings.isUnicode", {
        is: () => !settings.isUnicode,
        then: Yup.mixed()
            .test("FileNameTest",
                `Invalid document name`,
                (file) => {
                    if (file) {
                        if (file.isValid !== undefined && file.isValid !== null && !file.isValid) return false;
                        file.isValid = file.name.match(characterRestrictionInDocNamesRegExp) !== null;
                        if (!file.isValid) file.errMessage = "Invalid document name";
                        return file.isValid;
                    }
                })
    }));
const checkDokument = (settings) => (Yup.mixed()
    .test("FileSize", `Document must not be larger than ${getFileSizeAsString(settings.applicationFileSizeLimit)}.`, (file) => {
        if (file) {
            if (file.isValid === undefined || file.isValid === null) {
                file.isValid = true;
            }
            else {
                file.isValid = file.size < settings.applicationFileSizeLimit;
                if (!file.isValid) file.errMessage = `Document must not be larger than ${getFileSizeAsString(settings.applicationFileSizeLimit)}.`;
            }
            return file.isValid;
        }
        else {
            return true;
        }
    })
    .test("NullByteTest", "The file must not be empty.", (file) => {
        if (file) {
            if (file.isValid === undefined || file.isValid === null) {
                file.isValid = true;
            }
            else if (file.isValid) {
                file.isValid = file.size > 0;
                if (!file.isValid) file.errMessage = "The file must not be empty.";
            }
            return file.isValid;
        }
        else {
            return true;
        }
    })
    .test("FileFormat", `The following file formats are allowed: ${settings.applicationPermittedExtensions.join(" ")}`, (file) => {
        if (file) {
            if (file.isValid === undefined || file.isValid === null) {
                file.isValid = true;
            }
            else if (file.isValid) {
                file.isValid = settings.applicationPermittedMimeTypes.includes(file.type);
                if (!file.isValid) file.errMessage = `The following file formats are allowed: ${settings.applicationPermittedExtensions.join(" ")}`;
            }
            return file.isValid;
        }
        else {
            return true;
        }
    })
    .when("settings.isUnicode", {
        is: () => !settings.isUnicode,
        then: Yup.mixed()
            .test("FileNameTest",
                `Invalid document name`,
                (file) => {
                    if (file) {
                        if (file.isValid === undefined || file.isValid === null) {
                            file.isValid = true;
                        }
                        else if (file.isValid) {
                            file.isValid = file.name.match(characterRestrictionInDocNamesRegExp) !== null;
                            if (!file.isValid) file.errMessage = "Invalid document name";
                        }
                        return file.isValid;
                    }
                    else {
                        return true;
                    }
                })
    }));

export const Schema = settings => (Yup.object().shape({
    cvFile: checkRequiredDokument(settings),
    letterFile: checkDokument(settings),
    additionalFiles: Yup.array()
        .nullable()
        .test("FileSize",
            `The documents must be no more than ${applicationFileNumberLimit} files and together no larger than ${getFileSizeAsString(settings.applicationFileSizeLimit)}.`,
            (files) => {
                if (files && files.length > 0) {
                    let totalSize: number = files.reduce((accumulator, currentFile) => accumulator + currentFile.size, 0);
                    return totalSize <= settings.applicationFileSizeLimit && files.length <= applicationFileNumberLimit;
                } else {
                    return true;
                }
            })
        .test("FileFormat",
            `The following file formats are allowed: ${applicationPermittedExtensions.join(" ")}`,
            (files) => {
                if (files && files.length > 0) {
                    let allValid = true;
                    for (let file of files) {
                        if (file.isValid === undefined || file.isValid === null) file.isValid = true;
                        if (!applicationPermittedMimeTypes.includes(file.type)) {
                            file.isValid = false;
                            file.errMessage = `The following file formats are allowed: ${applicationPermittedExtensions.join(" ")}`;
                            allValid = false;
                        }
                    }
                    return allValid;
                } else {
                    return true;
                }
            })
        .when("settings.isUnicode", {
            is: () => !settings.isUnicode,
            then: Yup.array().nullable()
                .test("FileNameTest",
                    `Invalid document name`,
                    (files) => {
                        if (files && files.length > 0) {
                            let allValid = true;
                            for (let file of files) {
                                if (file.isValid === undefined || file.isValid === null) file.isValid = true;
                                if (!file.name.match(characterRestrictionInDocNamesRegExp)) {
                                    file.isValid = false;
                                    file.errMessage = `Invalid document name`;
                                    allValid = false;
                                }
                            }
                            return allValid;
                        } else {
                            return true;
                        }
                    })
        }),
}));

class FileExt extends File {
    isValid: boolean = true;
    errMessage: string = "";
}

export const DocumentsSubForm = ({ form: { values, setFieldTouched, setFieldValue, handleBlur, handleChange, touched, errors }, ...props }) => {
    const { colors, settings } = props;
    const fileInputLetter = useRef<HTMLInputElement>(null);
    const fileInputCV = useRef<HTMLInputElement>(null);
    const fileInputAdditionalFiles = useRef<HTMLInputElement>(null);
    const [fileNameLetter, setFileNameLetter] = useState("No file selected.");
    const [fileNamesAdditionalFiles, setFileNamesAdditionalFiles] = useState("No file selected.");
    const [fileNameCV, setFileNameCV] = useState("No file selected.");
    const [cvDocument, setCvDocument] = useState<FileExt>();
    const [letterDocument, setLetterDocument] = useState<FileExt>();
    const [additionalDocuments, setAdditionalDocuments] = useState<FileExt[]>([]);

    useEffect(() => {
        setFileNameLetter("No file selected");
        setFileNameCV("No file slected");
        setFileNamesAdditionalFiles("No file slected");
    }, []);

    function getTotalSizeOfAdditionalDocs(): number {
        if (!additionalDocuments || additionalDocuments?.length === 0) return 0

        let totalSize: number = 0
        for (let i = 0; i < additionalDocuments.length; i++) {
            const element = additionalDocuments[i];
            totalSize += element.size
        }
        return totalSize
    }

    function getTotalSizeOfDocs(): number {
        let totalSizeOfAddititionDocs = getTotalSizeOfAdditionalDocs();
        let cvDocumentSize = cvDocument ? cvDocument.size : 0;
        let letterDocumentSize = letterDocument ? letterDocument.size : 0;
        return totalSizeOfAddititionDocs + cvDocumentSize + letterDocumentSize;
    }

    return (
        <Row>

            <Form.Group className="mb-3" controlId="formLetterDoc">
                <Form.Label>Cover letter {letterDocument && "(" + getFileSizeAsString(letterDocument.size) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    name="letterFile"
                    ref={fileInputLetter}
                    onChange={(event) => {
                        if (event.target.files[0]) {
                            setLetterDocument(event.target.files[0]);
                            setFieldTouched("letterFile");
                            setFieldValue("letterFile", event.target.files[0] || undefined);
                            setFileNameLetter(event.target.files[0].name);
                        }
                    }}
                    style={{ display: 'none' }}
                    isValid={touched.letterFile && !errors.letterFile}
                    isInvalid={touched.letterFile && errors.letterFile}
                    accept={settings.applicationPermittedExtensions}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.letterFile}
                </Form.Control.Feedback>
            </Form.Group>
            <InputGroup className="mb-3">
                <CustomFileControlButton className={"FormLetterFileControlButton"}
                    onClick={() => fileInputLetter.current && fileInputLetter.current.click()}
                    colors={colors}
                // isValid={touched.letterFile && !errors.letterFile}
                // isInvalid={touched.letterFile && errors.letterFile}
                >
                    Browse...
                </CustomFileControlButton>
                <CustomFileControlText className={"FormLetterFileControlText"}
                    onClick={() => fileInputLetter.current && fileInputLetter.current.click()}
                    colors={colors}
                    value={fileNameLetter}
                    type="text"
                    isValid={touched.letterFile && !errors.letterFile}
                    isInvalid={touched.letterFile && errors.letterFile}
                    readOnly
                />
            </InputGroup>
            <Form.Group className="mb-3" controlId="formCvDoc">
                <Form.Label>CV* {cvDocument && "(" + getFileSizeAsString(cvDocument.size) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    required
                    name="cvFile"
                    ref={fileInputCV}
                    onChange={(event) => {
                        if (event.target.files[0]) {
                            setCvDocument(event.target.files[0]);
                            setFieldTouched("cvFile");
                            setFieldValue("cvFile", event.target.files[0] || undefined);
                            setFileNameCV(event.target.files[0].name);
                        }
                    }}
                    style={{ display: 'none' }}
                    isValid={touched.cvFile && !errors.cvFile}
                    isInvalid={touched.cvFile && errors.cvFile}
                    accept={applicationPermittedExtensions}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.cvFile}
                </Form.Control.Feedback>
            </Form.Group>
            <InputGroup className="mb-3">
                <CustomFileControlButton className={"FormCVFileControlButton"}
                    onClick={() => fileInputCV.current && fileInputCV.current.click()}
                    colors={colors}
                // isValid={touched.cvFile && !errors.cvFile}
                // isInvalid={touched.cvFile && errors.cvFile}
                >
                    Browse...
                </CustomFileControlButton>
                <CustomFileControlText className={"FormCVFileControlText"}
                    onClick={() => fileInputCV.current && fileInputCV.current.click()}
                    colors={colors}
                    value={fileNameCV}
                    type="text"
                    isValid={touched.cvFile && !errors.cvFile}
                    isInvalid={touched.cvFile && errors.cvFile}
                    readOnly
                />
            </InputGroup>
            <Form.Group className="mb-3" controlId="formAdditionalFiles">
                <Form.Label>Certificates (if applicable) {additionalDocuments && getTotalSizeOfAdditionalDocs() > 0 && "(" + getFileSizeAsString(getTotalSizeOfAdditionalDocs()) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    multiple
                    name="additionalFiles"
                    ref={fileInputAdditionalFiles}
                    onChange={(event) => {

                        if (event.target.files?.length) {
                            let files: FileExt[] = [];
                            if (additionalDocuments.length > 0) {
                                files = additionalDocuments.slice();
                            }
                            files.push(...Array.from<FileExt>(event.target.files));

                            setAdditionalDocuments(files);
                            setFieldTouched("additionalFiles");
                            setFieldValue("additionalFiles", files || undefined);
                            let numberOfFiles = event.target.files.length;
                            if (numberOfFiles == 1) {
                                setFileNamesAdditionalFiles(event.target.files[0].name);
                            }
                            if (numberOfFiles > 1) {
                                setFileNamesAdditionalFiles(numberOfFiles.toString() + " files selected.");
                            }
                        }

                    }}
                    style={{ display: 'none' }}
                    isValid={touched.additionalFiles && !errors.additionalFiles}
                    isInvalid={errors.additionalFiles}
                    accept={applicationPermittedExtensions} />
                <Form.Control.Feedback type="invalid">
                    {errors.additionalFiles}
                </Form.Control.Feedback>
                <InputGroup className="mb-3">
                    <CustomFileControlButton className={"FormAdditionalFilesControlButton--ChooseFileButton"}
                        onClick={() => fileInputAdditionalFiles.current && fileInputAdditionalFiles.current.click()}
                        colors={colors}
                    // isValid={touched.additionalFiles && !errors.additionalFiles}
                    // isInvalid={touched.additionalFiles && errors.additionalFiles}
                    >
                        Bowse...
                    </CustomFileControlButton>
                    <CustomFileControlText className={"FormAdditionalFilesControlText form-control-static FormLastNameControl"}
                        onClick={() => fileInputAdditionalFiles.current && fileInputAdditionalFiles.current.click()}
                        colors={colors}
                        value={fileNamesAdditionalFiles}
                        type="text"
                        isValid={touched.additionalFiles && !errors.additionalFiles}
                        isInvalid={touched.additionalFiles && errors.additionalFiles}
                        readOnly
                    />
                </InputGroup>
                <Form.Text muted style={{ display: errors.additionalFiles ? "none" : "flex" }}>
                    Multiple data with max. {applicationFileNumberLimit} documents. Additional documents are added dynamically.
                    Maximum file size: {getFileSizeAsString(settings.applicationFileSizeLimit)}. Permitted file types: pdf
                </Form.Text>
            </Form.Group>

            <div style={{ marginBottom: "16px" }}>
                <Collapse in={getTotalSizeOfDocs() > 0}>
                    <Accordion>
                        <Accordion.Item eventKey="0">
                            <CustomAccordionButton colors={colors}>Selected files ({getFileSizeAsString(getTotalSizeOfDocs())})</CustomAccordionButton>
                            <Accordion.Body>
                                <Table>
                                    <tbody>
                                        {cvDocument &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={cvDocument.isValid ? "" : "text-danger"}><strong>{cvDocument.name}</strong></td>
                                                <td>{getFileSizeAsString(cvDocument.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setCvDocument(undefined);
                                                        setFieldTouched("cvFile");
                                                        setFieldValue("cvFile", undefined);

                                                        let inputElement = document.getElementById("formCvDoc") as HTMLInputElement;
                                                        inputElement.value = "";
                                                    }}
                                                >Remove</CustomLink></td>
                                                <td className="invalid" title={cvDocument.errMessage} style={{ visibility: cvDocument.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {letterDocument &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={letterDocument.isValid ? "" : "text-danger"}><strong>{letterDocument.name}</strong></td>
                                                <td>{getFileSizeAsString(letterDocument.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setLetterDocument(undefined);
                                                        setFieldTouched("letterFile");
                                                        setFieldValue("letterFile", undefined);

                                                        let inputElement = document.getElementById("formLetterDoc") as HTMLInputElement;
                                                        inputElement.value = "";
                                                        setFileNameLetter("No file selected.");
                                                    }}
                                                >Remove</CustomLink></td>
                                                <td className="invalid" title={letterDocument.errMessage} style={{ visibility: letterDocument.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {additionalDocuments && Array.from(additionalDocuments).map((file: FileExt) =>
                                            <tr key={file.name}>
                                                <td style={{ wordBreak: "break-all" }} className={file.isValid === undefined || file.isValid ? "" : "text-danger"}>{file.name}</td>
                                                <td>{getFileSizeAsString(file.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        let pos = -1;
                                                        for (let i = 0; i < additionalDocuments.length; i++) {
                                                            const f: FileExt = additionalDocuments[i]
                                                            if (f.name === file.name) {
                                                                pos = i;
                                                                break;
                                                            }
                                                        }

                                                        if (pos > -1) {
                                                            let files: FileExt[] = [];
                                                            files = additionalDocuments.slice();
                                                            files.splice(pos, 1);

                                                            setAdditionalDocuments(files);
                                                            setFieldValue("additionalFiles", files || undefined);

                                                            if (files.length === 0) {
                                                                let inputElement = document.getElementById("formAdditionalFiles") as HTMLInputElement;
                                                                inputElement.value = "";
                                                            }
                                                        }
                                                    }}
                                                >Remove</CustomLink></td>
                                                <td className="invalid" title={file.errMessage} style={{ visibility: file.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </Collapse>
            </div>
        </Row>
    );
};
